import '@/styles/notifications-layout.css';
import { useNavigate } from 'react-router-dom';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { resolveMediaUrl } from '@/lib/assetUrl';
import {
  formatNotificationDate,
  useNotifications,
  type InboxNotification,
} from '@/hooks/useNotifications';

function NotificationIcon({ notification }: { notification: InboxNotification }) {
  if (notification.icon_class) {
    return <i className={notification.icon_class} />;
  }
  if (notification.icon_url) {
    return (
      <img
        src={resolveMediaUrl(notification.icon_url)}
        alt=""
        style={{ width: 32, height: 32 }}
      />
    );
  }
  return <i className="ph-light ph-bell" />;
}

function NotificationCard({ notification }: { notification: InboxNotification }) {
  const navigate = useNavigate();
  const isRead = (notification.is_read ?? 0) === 1;

  return (
    <div
      className={`notifications-item ${isRead ? 'read' : 'unread'}`}
      data-notification-id={notification.id}
      onClick={() => navigate(`/notifications/${notification.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/notifications/${notification.id}`);
        }
      }}
    >
      <div className="notifications-item-content">
        <div className="notifications-item-top">
          <div className="notifications-item-icon">
            <NotificationIcon notification={notification} />
          </div>
          <div className="notifications-item-info">
            <h4 className="notifications-item-title">{notification.title}</h4>
            <span className="notifications-item-type">{notification.type_name ?? ''}</span>
          </div>
          <div className="notifications-item-date">{formatNotificationDate(notification.publish_at)}</div>
          <div className="notifications-item-status">
            {isRead ? (
              <i className="fa-solid fa-check" aria-label="Read" />
            ) : (
              <i className="fa-solid fa-envelope" aria-label="Unread" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotificationsPage() {
  const { t } = useLocale();
  const { data: notifications = [], isLoading, error } = useNotifications();

  return (
    <div className="container-ingame notifications-page">
      <div className="notifications-container">
        <SimpleMenu title={t('notifications_title')} />

        <div className="notifications-body">
          {error ? (
            <div
              className="alert alert-danger"
              style={{
                marginBottom: 20,
                padding: 15,
                background: '#c00',
                color: '#fff',
                borderRadius: 5,
              }}
            >
              {t('notifications_error_load')}
            </div>
          ) : null}


          <div className="notifications-list">
            {isLoading ? (
              <div className="notifications-empty" style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                {t('notifications_loading')}
              </div>
            ) : notifications.length === 0 ? (
              <div className="notifications-empty" style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                {t('notifications_no_notifications')}
              </div>
            ) : (
              notifications.map((n) => <NotificationCard key={n.id} notification={n} />)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
